import React, { useCallback, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Collapse from "@mui/material/Collapse";
import Chip from "@mui/material/Chip";
import Avatar from "@mui/material/Avatar";
import AddIcon from "@mui/icons-material/Add";
import Check from "@mui/icons-material/Check";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUp from "@mui/icons-material/KeyboardArrowUp";
import PersonOutline from "@mui/icons-material/PersonOutline";
import { useDispatchApp, useSelectorApp } from "../../../lib/redux";
import { useTranslationApp } from "../../../lib/i18next";
import useButtonActionPlatform from "../../../hooks/useButtonActionPlatform";
import { logoPlatform } from "../../../utils/string";
import {
  CHIP_SEARCH_COMPONENT_COLOR,
  INPUT_COLOR_BORDER,
} from "../../../utils/colors";
import {
  ALERT_ICON_TYPE_SUCCESS,
  BORDER_RADIUS,
  GOOGLE,
  LINKEDIN,
  META,
  TIKTOK,
  TIKTOK_BUSINESS,
} from "../../../utils/constants";
import {
  ALERT_DESCRIPTION_DISCONNECT_FACEBOOK,
  ALERT_DESCRIPTION_DISCONNECT_GOOGLE,
  ALERT_DESCRIPTION_DISCONNECT_LINKEDIN,
  ALERT_DESCRIPTION_DISCONNECT_TIKTOK_BUSINESS,
  ARE_YOU_SURE,
  BUTTON_DISCONNECT,
  BUTTON_DISCONNECT_ACCOUNT,
  CONNECTED_ACCOUNTS_ACCORDION_COLLAPSE,
  CONNECTED_ACCOUNTS_ACCORDION_EXPAND,
  CONNECTED_ACCOUNTS_BUTTON_CONNECT,
  CONNECTED_ACCOUNTS_CONNECT_MORE_PLATFORMS,
  CONNECTED_ACCOUNTS_INTEGRATIONS_COUNT,
  CONNECTED_ACCOUNTS_INTEGRATIONS_COUNT_SINGULAR,
  CONNECTED_ACCOUNTS_PLATFORM_GOOGLE,
  CONNECTED_ACCOUNTS_PLATFORM_LINKEDIN,
  CONNECTED_ACCOUNTS_PLATFORM_META,
  CONNECTED_ACCOUNTS_PLATFORM_TIKTOK_BUSINESS,
  DISCONNECTED_ACCOUNT,
  READY,
} from "../../../i18n/keysTranslations";
import { ConfirmAlert, SimpleAlert } from "../../../components/Alerts/Alerts";
import Button from "../../../components/Buttons/Button";
import PlatformDefaultsSettingsSection from "./PlatformDefaults/PlatformDefaultsSettingsSection";
import {
  startRevokeFacebookAccount,
  startRevokeGoogleAdsAccount,
  startRevokeLinkedInAdsAccount,
  startRevokeTiktokBusinessAccount,
} from "../../../actions/user";

const PLATFORM_CONFIG = [
  {
    integrationsKey: "facebook",
    actionKey: META,
    logoKey: META,
    nameKey: CONNECTED_ACCOUNTS_PLATFORM_META,
  },
  {
    integrationsKey: TIKTOK_BUSINESS,
    actionKey: TIKTOK,
    logoKey: TIKTOK,
    nameKey: CONNECTED_ACCOUNTS_PLATFORM_TIKTOK_BUSINESS,
  },
  {
    integrationsKey: "google",
    actionKey: GOOGLE,
    logoKey: GOOGLE,
    nameKey: CONNECTED_ACCOUNTS_PLATFORM_GOOGLE,
  },
  {
    integrationsKey: LINKEDIN,
    actionKey: LINKEDIN,
    logoKey: LINKEDIN,
    nameKey: CONNECTED_ACCOUNTS_PLATFORM_LINKEDIN,
  },
];

/** Profile name + check: check stays beside the label; ellipsis only when width is constrained */
const PROFILE_NAME_WITH_CHECK_ROW_SX = {
  display: "inline-flex",
  alignItems: "center",
  gap: 0.75,
  minWidth: 0,
  maxWidth: "100%",
};

const PROFILE_NAME_TRUNCATE_SX = {
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
};

/** Platform account cards: full width on mobile/tablet; half width on large screens */
const PLATFORM_ACCOUNT_CARD_SHELL_SX = {
  borderRadius: 3,
  border: `1px solid ${INPUT_COLOR_BORDER}`,
  bgcolor: "background.paper",
  overflow: "visible",
  width: "100%",
  maxWidth: { xs: "100%", lg: "50%" },
  alignSelf: "flex-start",
};

/** Compact primary/secondary controls for dense platform rows (connect, disconnect) */
const DISCONNECT_BUTTON_ROW_SX = {
  height: 38,
  minHeight: 38,
  minWidth: 120,
  py: "6px",
  textTransform: "none",
  flexShrink: 0,
};

const getActiveIntegrations = (map) =>
  Object.values(map || {}).filter((integration) => integration?.isActive);

/** One row per active OAuth integration (accordion list). */
const buildExpandProfileRows = ({ activeIntegrations }) =>
  activeIntegrations.map((integration) => ({
    rowKey: `oauth:${integration.id}`,
    title: integration.name || integration.id || "",
    revokeIntegrationId: integration.id,
  }));

/** Green circular check beside each connected profile name */
const ConnectedProfileCheckBadge = () => (
  <Box
    component="span"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      width: 16,
      height: 16,
      borderRadius: "50%",
      bgcolor: "success.main",
      border: "2px solid",
      borderColor: "background.paper",
      color: "#ffffff !important",
      "& .MuiSvgIcon-root": {
        color: "#ffffff !important",
        fill: "#ffffff !important",
      },
      "& .MuiSvgIcon-root path": {
        fill: "#ffffff !important",
      },
    }}
  >
    <Check
      sx={{
        fontSize: 11,
        color: "#ffffff !important",
        fill: "#ffffff !important",
        "& path": {
          fill: "#ffffff !important",
        },
      }}
    />
  </Box>
);

const ConnectedAccountsPanel = () => {
  const dispatch = useDispatchApp();
  const { t } = useTranslationApp();
  const { isLoading, integrations } = useSelectorApp((state) => state.user);
  const buttonActions = useButtonActionPlatform(true);

  const [expandedKeys, setExpandedKeys] = useState(() => new Set());

  const [revokingIntegrationID, setRevokingIntegrationID] = useState(null);

  const platformRows = useMemo(
    () =>
      PLATFORM_CONFIG.map((row) => {
        const map = integrations?.[row.integrationsKey] || {};
        const active = getActiveIntegrations(map);
        return { ...row, activeIntegrations: active };
      }),
    [integrations]
  );

  const hasAnyConnection = useMemo(
    () => platformRows.some((row) => row.activeIntegrations.length > 0),
    [platformRows]
  );

  const connectedRows = useMemo(
    () => platformRows.filter((row) => row.activeIntegrations.length > 0),
    [platformRows]
  );

  const disconnectedRows = useMemo(
    () => platformRows.filter((row) => row.activeIntegrations.length === 0),
    [platformRows]
  );

  const getDisconnectDescription = useCallback(
    (actionKey) => {
      if (actionKey === META) return t(ALERT_DESCRIPTION_DISCONNECT_FACEBOOK);
      if (actionKey === GOOGLE) return t(ALERT_DESCRIPTION_DISCONNECT_GOOGLE);
      if (actionKey === TIKTOK)
        return t(ALERT_DESCRIPTION_DISCONNECT_TIKTOK_BUSINESS);
      if (actionKey === LINKEDIN)
        return t(ALERT_DESCRIPTION_DISCONNECT_LINKEDIN);
      return "";
    },
    [t]
  );

  const onRevokeIntegration = useCallback(
    async ({ actionKey, integrationId }) => {
      const confirm = await ConfirmAlert({
        title: t(ARE_YOU_SURE),
        text: getDisconnectDescription(actionKey),
        confirmButtonText: t(BUTTON_DISCONNECT_ACCOUNT),
      });

      if (!confirm.isConfirmed) {
        return;
      }

      if (actionKey !== META) {
        setRevokingIntegrationID(integrationId);
      }

      let ok = false;

      try {
        if (actionKey === META) {
          ok = await dispatch(
            startRevokeFacebookAccount({
              id: integrationId,
              onChangeLoading: (loading) => {
                setRevokingIntegrationID(loading ? integrationId : null);
              },
              onCallbackRevoked: () => {
                SimpleAlert({
                  title: t(DISCONNECTED_ACCOUNT),
                  icon: ALERT_ICON_TYPE_SUCCESS,
                });
              },
            })
          );
        } else if (actionKey === GOOGLE) {
          ok = await dispatch(
            startRevokeGoogleAdsAccount({ id: integrationId })
          );
        } else if (actionKey === TIKTOK) {
          ok = await dispatch(
            startRevokeTiktokBusinessAccount({ id: integrationId })
          );
        } else if (actionKey === LINKEDIN) {
          ok = await dispatch(
            startRevokeLinkedInAdsAccount({ id: integrationId })
          );
        }

        if (ok && actionKey !== META) {
          SimpleAlert({
            title: t(DISCONNECTED_ACCOUNT),
            icon: ALERT_ICON_TYPE_SUCCESS,
          });
        }
      } finally {
        if (actionKey !== META) {
          setRevokingIntegrationID(null);
        }
      }
    },
    [dispatch, getDisconnectDescription, t]
  );

  const onToggleExpand = (key) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  if (isLoading) return null;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        width: "100%",
      }}
    >
      {!hasAnyConnection ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {PLATFORM_CONFIG.map((row) => (
            <Box key={row.integrationsKey} sx={PLATFORM_ACCOUNT_CARD_SHELL_SX}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 2,
                  py: 1.5,
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    minWidth: 0,
                    flex: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "grey.300",
                      flexShrink: 0,
                    }}
                  >
                    <Avatar
                      src={logoPlatform(row.logoKey)}
                      variant="square"
                      alt=""
                      sx={{
                        width: 22,
                        height: 22,
                        bgcolor: "transparent",
                        "& .MuiAvatar-img": { objectFit: "contain" },
                      }}
                    />
                  </Box>
                  <Typography
                    variant="subtitle2"
                    noWrap
                    sx={{ fontWeight: 600, minWidth: 0 }}
                  >
                    {t(row.nameKey)}
                  </Typography>
                </Box>
                <Box sx={{ flexShrink: 0 }}>
                  {React.cloneElement(buttonActions[row.actionKey], {
                    id: `connected-accounts-empty-connect-${row.integrationsKey}`,
                    type: "button",
                    mode: "verify",
                    text: CONNECTED_ACCOUNTS_BUTTON_CONNECT,
                    showLogo: false,
                    sx: DISCONNECT_BUTTON_ROW_SX,
                  })}
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      ) : (
        <>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {connectedRows.map((row) => {
              const { activeIntegrations, integrationsKey, actionKey } = row;
              const expandRows = buildExpandProfileRows({ activeIntegrations });
              const isExpanded = expandedKeys.has(integrationsKey);

              return (
                <Box key={integrationsKey} sx={PLATFORM_ACCOUNT_CARD_SHELL_SX}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      px: 2,
                      py: 1.5,
                      gap: 1,
                      cursor: "pointer",
                    }}
                    onClick={() => onToggleExpand(integrationsKey)}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        minWidth: 0,
                        flex: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: "grey.300",
                          flexShrink: 0,
                        }}
                      >
                        <Avatar
                          src={logoPlatform(row.logoKey)}
                          variant="square"
                          alt=""
                          sx={{
                            width: 22,
                            height: 22,
                            bgcolor: "transparent",
                            "& .MuiAvatar-img": { objectFit: "contain" },
                          }}
                        />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                            flexWrap: "wrap",
                            minWidth: 0,
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            component="span"
                            noWrap
                            sx={{ fontWeight: 600 }}
                          >
                            {t(row.nameKey)}
                          </Typography>
                          <Chip
                            size="small"
                            label={
                              expandRows.length === 1
                                ? t(
                                    CONNECTED_ACCOUNTS_INTEGRATIONS_COUNT_SINGULAR
                                  )
                                : t(CONNECTED_ACCOUNTS_INTEGRATIONS_COUNT, {
                                    count: expandRows.length,
                                  })
                            }
                            sx={{
                              height: 22,
                              fontSize: "0.7rem",
                              fontWeight: 600,
                              backgroundColor: CHIP_SEARCH_COMPONENT_COLOR,
                              border: `1px solid ${INPUT_COLOR_BORDER}`,
                              color: "text.secondary",
                              "& .MuiChip-label": { px: 1 },
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexShrink: 0,
                      }}
                    >
                      <Box
                        component="span"
                        onClick={(e) => e.stopPropagation()}
                        sx={{ display: "inline-flex" }}
                      >
                        {React.cloneElement(buttonActions[row.actionKey], {
                          id: `connected-accounts-add-${row.integrationsKey}`,
                          type: "icon",
                          mode: "add",
                          icon: (
                            <AddIcon
                              sx={{ color: "text.secondary", fontSize: 20 }}
                            />
                          ),
                          disableRipple: true,
                          sx: {
                            color: "text.secondary",
                          },
                          onClick: () => {
                            SimpleAlert({
                              title: t(READY),
                              icon: ALERT_ICON_TYPE_SUCCESS,
                            });
                          },
                        })}
                      </Box>
                      <IconButton
                        size="small"
                        aria-expanded={isExpanded}
                        aria-label={
                          isExpanded
                            ? t(CONNECTED_ACCOUNTS_ACCORDION_COLLAPSE)
                            : t(CONNECTED_ACCOUNTS_ACCORDION_EXPAND)
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          onToggleExpand(integrationsKey);
                        }}
                        sx={{ color: "text.secondary" }}
                      >
                        {isExpanded ? (
                          <KeyboardArrowUp fontSize="small" />
                        ) : (
                          <KeyboardArrowDown fontSize="small" />
                        )}
                      </IconButton>
                    </Box>
                  </Box>

                  <Collapse
                    in={isExpanded}
                    timeout="auto"
                    sx={{ width: "100%", display: "block" }}
                  >
                    <Box
                      sx={{
                        borderTop: `1px solid ${INPUT_COLOR_BORDER}`,
                        bgcolor: "background.paper",
                        width: "100%",
                        display: "block",
                        overflow: "hidden",
                      }}
                    >
                      {expandRows.map((profileRow, index) => (
                        <Box
                          key={profileRow.rowKey}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            pl: { xs: 2, sm: 8 },
                            pr: 2,
                            py: 1.5,
                            ...(index < expandRows.length - 1
                              ? {
                                  borderBottom: `1px solid ${INPUT_COLOR_BORDER}`,
                                }
                              : {}),
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                              minWidth: 0,
                            }}
                          >
                            <Avatar
                              sx={{
                                width: 32,
                                height: 32,
                                bgcolor: "grey.300",
                              }}
                            >
                              <PersonOutline
                                sx={{
                                  fontSize: 20,
                                  color: "text.secondary",
                                }}
                              />
                            </Avatar>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Box sx={PROFILE_NAME_WITH_CHECK_ROW_SX}>
                                <Typography
                                  variant="body2"
                                  fontWeight={600}
                                  noWrap
                                  sx={PROFILE_NAME_TRUNCATE_SX}
                                >
                                  {profileRow.title}
                                </Typography>
                                <ConnectedProfileCheckBadge />
                              </Box>
                            </Box>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              flexShrink: 0,
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Button
                              id={`connected-accounts-disconnect-${profileRow.rowKey}`}
                              color="secondary"
                              size="small"
                              loading={
                                Boolean(revokingIntegrationID) &&
                                revokingIntegrationID ===
                                  profileRow.revokeIntegrationId
                              }
                              disabled={!profileRow.revokeIntegrationId}
                              onClick={(e) => {
                                e.stopPropagation();
                                void onRevokeIntegration({
                                  actionKey,
                                  integrationId: profileRow.revokeIntegrationId,
                                });
                              }}
                              sx={DISCONNECT_BUTTON_ROW_SX}
                            >
                              {t(BUTTON_DISCONNECT)}
                            </Button>
                          </Box>
                        </Box>
                      ))}
                      <PlatformDefaultsSettingsSection platform={actionKey} />
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
          </Box>

          {disconnectedRows.length > 0 ? (
            <Box sx={{ pt: 1 }}>
              <Typography
                variant="body2"
                fontWeight={600}
                color="text.secondary"
                sx={{ mb: 1.5 }}
              >
                {t(CONNECTED_ACCOUNTS_CONNECT_MORE_PLATFORMS)}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                {disconnectedRows.map((row) => (
                  <Box key={row.integrationsKey}>
                    {React.cloneElement(buttonActions[row.actionKey], {
                      id: `connected-accounts-invite-${row.integrationsKey}`,
                      type: "button",
                      mode: "verify",
                      sx: {
                        borderRadius: BORDER_RADIUS,
                        borderStyle: "dashed",
                        textTransform: "none",
                      },
                    })}
                  </Box>
                ))}
              </Box>
            </Box>
          ) : null}
        </>
      )}
    </Box>
  );
};

export default ConnectedAccountsPanel;
